import type { Dispatch, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { resourceListTag, resourceTag } from '@krp/client-runtime'

import { newOperationId } from '../../../shared/model/operationIds'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { VERSION_PROBE_LIMIT } from '../model/limits'
import type { PortOutcome, ProductsPorts } from '../model/ports'
import type { ProductOperation, ProductOperationResult } from '../model/types'
import {
  ONEC_PRODUCT_TAG,
  onecAddProductBarcode,
  onecCreateProduct,
  onecDeleteProductBarcode,
  onecMarkProductDeleted,
  onecProductCard,
  onecProductSearch,
  onecSetMainProductBarcode,
  onecSyncCapabilities,
  onecTriggerCatalogSync,
  onecUpdateProduct,
  productsApi,
} from './productsApi'

/**
 * Порты товаров по умолчанию: endpoints общего `baseApi`. Listener получает
 * dispatch стора приложения (с thunk middleware), поэтому запросы идут
 * через ЕДИНСТВЕННЫЙ transport и попадают в общий кэш.
 */
type ThunkCapable = ThunkDispatch<unknown, unknown, UnknownAction>

async function settle<T, R>(
  started: { unwrap: () => Promise<T>; reset?: () => void },
  map: (value: T) => R,
): Promise<PortOutcome<R>> {
  try {
    return { ok: true, value: map(await started.unwrap()) }
  } catch (error) {
    return { ok: false, error }
  } finally {
    // Итог команды — у операции; запись мутации в кэше не нужна.
    started.reset?.()
  }
}

function send(run: ThunkCapable, operation: ProductOperation): Promise<PortOutcome<ProductOperationResult>> {
  const identity = {
    operationId: operation.operationId,
    idempotencyKey: operation.idempotencyKey,
    expectedConfigurationVersion: operation.expectedConfigurationVersion,
  }
  const { resourceId, target: refKey, payload } = operation
  switch (operation.action) {
    case 'create': {
      if (payload.kind !== 'create') break
      const data = { name: payload.name, article: payload.article, barcode: payload.barcode }
      return settle(run(onecCreateProduct.initiate({ resourceId, identity, data })), (answer) => ({
        kind: 'created' as const,
        ...answer.data,
      }))
    }
    case 'update': {
      if (payload.kind !== 'update') break
      const data = {
        ...(payload.name !== undefined ? { name: payload.name } : {}),
        ...(payload.article !== undefined ? { article: payload.article } : {}),
      }
      return settle(run(onecUpdateProduct.initiate({ resourceId, refKey, identity, data })), (answer) => ({
        kind: 'updated' as const,
        changed: answer.data.changed,
      }))
    }
    case 'delete':
      return settle(run(onecMarkProductDeleted.initiate({ resourceId, refKey, identity, data: null })), (answer) => ({
        kind: 'deleted' as const,
        deleted: answer.data.deleted,
      }))
    case 'barcode-add':
    case 'barcode-delete':
    case 'barcode-main': {
      if (payload.kind !== 'barcode') break
      const args = { resourceId, refKey, barcode: payload.barcode, identity, data: null }
      const endpoint =
        operation.action === 'barcode-add'
          ? onecAddProductBarcode
          : operation.action === 'barcode-delete'
            ? onecDeleteProductBarcode
            : onecSetMainProductBarcode
      return settle(run(endpoint.initiate(args)), (answer) => ({ kind: 'barcode' as const, ...answer.data }))
    }
    case 'catalog-refresh': {
      if (payload.kind !== 'catalog-refresh') break
      const args = { resourceId, refreshKind: payload.refreshKind, identity, data: null }
      return settle(run(onecTriggerCatalogSync.initiate(args)), (answer) => ({ kind: 'refreshed' as const, ...answer.data }))
    }
  }
  return Promise.resolve({ ok: false, error: { message: 'Команда записана в неожиданном виде' } })
}

export function createProductsPorts(dispatch: Dispatch<UnknownAction>): ProductsPorts {
  const run = dispatch as ThunkCapable
  return {
    newOperationId,
    now: () => new Date().toISOString(),
    readCard: (resourceId, refKey) =>
      settle(run(onecProductCard.initiate({ resourceId, refKey }, { forceRefetch: true, subscribe: false })), (value) => value),
    readSyncVersion: (resourceId) =>
      settle(run(onecSyncCapabilities.initiate({ resourceId }, { forceRefetch: true, subscribe: false })), (value) => value),
    readVersion: (resourceId, name) =>
      settle(run(onecProductSearch.initiate({ resourceId, q: name, limit: VERSION_PROBE_LIMIT }, { subscribe: false })), (value) => value.configurationVersion),
    send: (operation) => send(run, operation),
    reloadProduct: (resourceId, refKey) => {
      dispatch(productsApi.util.invalidateTags([resourceTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId), refKey)]))
    },
    reloadLists: (resourceId) => {
      dispatch(productsApi.util.invalidateTags([resourceListTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId))]))
    },
  }
}
